/**
 * TikTok in-app browser handoff gate.
 *
 * Detection itself runs synchronously in layout/theme.liquid (before this
 * file loads) so the gate can be shown before first paint. This file wires
 * up the gate's own controls (copy link / dismiss) and adds a defense-in-depth
 * safety net: if a visitor reaches a checkout action while still inside
 * TikTok's in-app browser (gate dismissed, or a device/version this UA check
 * doesn't catch), intercept the click/submit before it hits TikTok's known
 * checkout block and show the gate instead.
 *
 * The main Checkout button/form is same-document and safe to intercept with
 * a document-level listener (see CHECKOUT_TRIGGER_SELECTOR below). Shop Pay /
 * PayPal / Apple Pay / Google Pay are different: Shopify may render them as
 * iframes or web components it owns, so a parent-document click listener
 * cannot reliably catch a tap before it reaches them — the tap can hand off
 * to that boundary before our handler ever runs. For that region only, a
 * same-document overlay button (`.cosmora-tiktok-checkout-guard`, see
 * sections/main-cart-footer.liquid) sits on top and captures the tap itself.
 * See syncCheckoutGuards() below.
 */
(function () {
  function isTikTokIAB() {
    return !!window.__cosmoraTikTokIAB;
  }

  // The overlay covers the page visually, but without this, keyboard/screen
  // reader users could still tab or navigate into the storefront underneath
  // it. Mark everything else inert while the gate is showing.
  function setBackgroundInert(active) {
    var gate = document.getElementById('cosmora-tiktok-gate');
    Array.prototype.forEach.call(document.body.children, function (el) {
      if (el === gate) return;
      if (active) {
        el.setAttribute('aria-hidden', 'true');
        el.setAttribute('inert', '');
      } else {
        el.removeAttribute('aria-hidden');
        el.removeAttribute('inert');
      }
    });
  }

  function showGate() {
    document.documentElement.classList.add('cosmora-tiktok-gate-active');
    setBackgroundInert(true);
    var gate = document.getElementById('cosmora-tiktok-gate');
    if (gate && typeof gate.focus === 'function') {
      gate.focus();
    }
  }

  function dismissGateForSession() {
    document.documentElement.classList.remove('cosmora-tiktok-gate-active');
    setBackgroundInert(false);
    try {
      sessionStorage.setItem('cosmoraTiktokGateDismissed', '1');
    } catch (error) {
      /* Private/blocked storage: gate simply won't remember the dismissal. */
    }
  }

  var CHECKOUT_GUARD_SELECTOR = '.cosmora-tiktok-checkout-guard';

  // The guard button ships `hidden` from Liquid so it has zero presence
  // (no layout, no a11y node, not focusable) for every browser by default.
  // Un-hide it only for TikTok's in-app browser; leave it untouched — and
  // therefore inert — everywhere else, including Instagram/Facebook's
  // in-app browsers, Safari, Chrome, and desktop.
  function syncCheckoutGuards() {
    var active = isTikTokIAB();
    var guards = document.querySelectorAll(CHECKOUT_GUARD_SELECTOR);
    Array.prototype.forEach.call(guards, function (guard) {
      guard.hidden = !active;
    });
  }

  // Cart quantity/remove updates in cart.js replace the cart footer's
  // subtotal block via fetch + innerHTML, not the buttons block this guard
  // lives in, so in this theme today the guard node itself is never
  // recreated after load. This observer exists as the "minimum practical"
  // safety net for that regardless: if the wallet-button region is ever
  // re-rendered (now or by a future change), a freshly inserted guard node
  // still ships `hidden` from Liquid and needs the same one-line re-sync.
  // Event-driven (MutationObserver), scoped to the cart footer only — no
  // polling.
  function observeCheckoutGuards() {
    var cartFooter = document.getElementById('main-cart-footer');
    if (!cartFooter || !window.MutationObserver) return;
    var observer = new MutationObserver(syncCheckoutGuards);
    observer.observe(cartFooter, { childList: true, subtree: true });
  }

  // Delegated: works even if a guard node above gets replaced later, since
  // the listener lives on document rather than on the node instance.
  document.addEventListener(
    'click',
    function (event) {
      if (!isTikTokIAB()) return;
      var guard = event.target.closest && event.target.closest(CHECKOUT_GUARD_SELECTOR);
      if (!guard) return;
      event.preventDefault();
      showGate();
    },
    true
  );

  document.addEventListener(
    'DOMContentLoaded',
    function () {
      if (document.documentElement.classList.contains('cosmora-tiktok-gate-active')) {
        setBackgroundInert(true);
      }

      syncCheckoutGuards();
      observeCheckoutGuards();

      var copyButton = document.getElementById('cosmora-gate-copy');
      var dismissButton = document.getElementById('cosmora-gate-dismiss');
      var copyLabel = copyButton ? copyButton.querySelector('[data-copy-label]') : null;
      var defaultCopyLabel = copyLabel ? copyLabel.textContent : '';

      if (copyButton) {
        copyButton.addEventListener('click', function () {
          var url = window.location.href;

          var onCopied = function () {
            copyButton.setAttribute('data-copied', 'true');
            if (copyLabel) {
              copyLabel.textContent = copyButton.getAttribute('data-copied-label') || defaultCopyLabel;
            }
            setTimeout(function () {
              copyButton.removeAttribute('data-copied');
              if (copyLabel) {
                copyLabel.textContent = defaultCopyLabel;
              }
            }, 2000);
          };

          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(url).then(onCopied).catch(function () {
              /* Clipboard write rejected (permissions/context) — leave the
                 button as-is; the ••• menu instructions above still work. */
            });
            return;
          }

          try {
            var input = document.createElement('input');
            input.value = url;
            input.setAttribute('readonly', '');
            input.style.position = 'fixed';
            input.style.opacity = '0';
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            document.body.removeChild(input);
            onCopied();
          } catch (error) {
            /* No reliable copy mechanism available; instructions still stand. */
          }
        });
      }

      if (dismissButton) {
        dismissButton.addEventListener('click', dismissGateForSession);
      }
    },
    { once: true }
  );

  var CHECKOUT_TRIGGER_SELECTOR = [
    '[name="checkout"]',
    '#CartDrawer-Checkout',
    '#checkout',
    '.cart__dynamic-checkout-buttons a',
    '.cart__dynamic-checkout-buttons button',
    '.additional-checkout-buttons a',
    '.additional-checkout-buttons button',
    '#dynamic-checkout-cart a',
    '#dynamic-checkout-cart button',
    '[data-shopify-buttoncontainer] a',
    '[data-shopify-buttoncontainer] button',
  ].join(', ');

  document.addEventListener(
    'click',
    function (event) {
      if (!isTikTokIAB()) return;
      var trigger = event.target.closest && event.target.closest(CHECKOUT_TRIGGER_SELECTOR);
      if (!trigger) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      showGate();
    },
    true
  );

  document.addEventListener(
    'submit',
    function (event) {
      if (!isTikTokIAB()) return;
      var submitter = event.submitter;
      if (submitter && submitter.name === 'checkout') {
        event.preventDefault();
        showGate();
      }
    },
    true
  );
})();
